import { EMITTER_CONSTANTS } from '@/shared/config';
import { isNotAllowedViewReport } from '@/entities/inbox';
import { isC1cStudy } from '@/entities/study';
import { zeroPad, emitter, generateFullName, dateToString } from '@/shared/lib';
import { useEmitter, useSetState } from '@/shared/lib/customHooks';
import type { MarkStatusReportSocketMessage, ReportUpdatedSocketMessage } from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  ClickedReport,
  Pagination,
  ReportsTableProps,
  ReportsTableState,
} from './reportsTable.types';

/**
 * Drives the study's reports table: tab selection, search, pagination and opening a report. Keeps
 * the formatted report list in state seeded from the study's reports, filters it by the active tab
 * and search term, opens the first verified study report when asked to (or raises the CardioOne
 * notice), and patches rows on mark-status and report-updated socket events.
 *
 * @param props - Study data, the shouldLoadVerifiedReport flag and its resetShouldLoadVerifiedReport
 * callback.
 * @returns An object carrying the table state and setter, the tab flags, the filtered report rows,
 * navigate, the click, table-change and search handlers, and the active search value.
 */
export const useReportsTable = ({
  data,
  shouldLoadVerifiedReport,
  resetShouldLoadVerifiedReport,
}: Pick<
  ReportsTableProps,
  'data' | 'shouldLoadVerifiedReport' | 'resetShouldLoadVerifiedReport'
>) => {
  const navigate = useNavigate();

  const [state, setState] = useSetState<ReportsTableState>({
    searchValueEvent: '',
    searchValueSummary: '',
    searchValueNotification: '',
    reportType: 'Summary reports',
    page: 1,
    pageSize: 10,
    listReports: [],
  });

  /**
   * Whether the reports table is currently showing the summary reports tab. Memoized comparison of state.reportType against 'Summary reports', recomputed when the selected report type changes.
   *
   * @returns True when the summary reports type is selected.
   */
  const isStudyReport = useMemo(() => state.reportType === 'Summary reports', [state.reportType]);

  /**
   * Whether the reports table is currently showing the notification reports tab. Memoized comparison of state.reportType against 'Notification reports', recomputed when the selected report type changes.
   *
   * @returns True when the notification reports type is selected.
   */
  const isNotificationReport = useMemo(
    () => state.reportType === 'Notification reports',
    [state.reportType],
  );

  /**
   * Flattens one report row together with the study context the reports table renders. Copies the report and adds the study and its friendly id, study type, patient and interpreting physician names, review flag, the event time and date-of-service strings rendered in the study and facility timezone, plus the facility, format versions, status fields and an action-button tag keyed by summary or event.
   *
   * @param report - Raw report returned by the API.
   * @returns Report row enriched with study data and formatted dates.
   */
  const formatReport = (report: any) => ({
    ...report,
    study: data,
    studyFid: data.friendlyId,
    eventFid: report.event?.friendlyId,
    studyType: data.studyType,
    patientName: generateFullName(data.info?.patient),
    interprettingPhysicianName: generateFullName(data.info?.interprettingPhysician),
    physicianInterpretation: report.physicianInterpretation,
    isReviewed: report.inbox?.isReviewed,
    eventTimeUTC: report.start,
    timezone: data.timezone,
    eventTime: dateToString({
      startStudyDate: data.start,
      isoDate: report.start,
      timezone: data.timezone,
      studyTimeOffset: data.studyTimeOffset,
      facilityCountry: data.facility?.contact?.country,
      facilityTimezone: data.facility?.timezone,
    }),
    dateOfService: `${dateToString({
      isoDate: report.start,
      timezone: data.timezone,
      studyTimeOffset: data.studyTimeOffset,
      format: 'MM/DD/YYYY',
    })} - ${dateToString({
      isoDate: report.stop,
      timezone: data.timezone,
      studyTimeOffset: data.studyTimeOffset,
      format: 'MM/DD/YYYY',
    })}`,
    hasClinicData: report.event?.hasClinicData,
    actionButtonTag: `${report.id}-${isStudyReport ? 'summary' : 'event'}`,
    facility: data.facility,
    formatVersion: report.formatVersion,
    aiFormatVersion: report.aiFormatVersion,
    statusReport: report.statusReport,
    lastStatusReportAt: report.lastStatusReportAt,
    isSavingStatus: report.isSavingStatus ?? false,
    profile: report.profile,
    isNonPhi: data.isNonPhi,
    lastModified: report.lastModified,
  });

  /**
   * Opens a report from the study's reports table. Emits the escalation popup with the facility's arrears when viewing is not allowed, raises the CardioOne report notice for a CardioOne study, and otherwise navigates to the report's inbox route.
   *
   * @param clickedReport - Report row that was clicked, with its facility and arrears.
   * @returns Nothing.
   */
  const onClickReport = (clickedReport: ClickedReport) => {
    if (isNotAllowedViewReport(clickedReport)) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, clickedReport.facility.arrears);
    } else if (isC1cStudy(data)) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_C1C_REPORT);
    } else {
      navigate(`/studies/inbox/${clickedReport.id}`);
    }
  };

  /**
   * Keeps the reports table's paging in state. Compares the incoming current page and page size against the stored values and writes back only those that changed.
   *
   * @param pagination - Table pagination carrying the current page and page size.
   * @returns Nothing.
   */
  const handleTableChange = (pagination: Pagination) => {
    const { current, pageSize } = pagination;
    const stateObject = {};
    if (current !== state.page) {
      _.assign(stateObject, { page: current });
    }
    if (pageSize !== state.pageSize) {
      _.assign(stateObject, { pageSize });
    }
    if (!_.isEmpty(stateObject)) {
      setState(stateObject);
    }
  };

  /**
   * Stores the reports-table search text against the report type currently shown. Writes the trimmed value into the summary, notification or event search field depending on state.reportType.
   *
   * @param name - Name of the search input; unused.
   * @param value - Search text entered by the user.
   * @returns Nothing.
   */
  const onChangeSearch = (name: string, value: string) => {
    if (state.reportType === 'Summary reports') {
      setState({ searchValueSummary: value?.trim() });
    } else if (state.reportType === 'Notification reports') {
      setState({ searchValueNotification: value?.trim() });
    } else {
      setState({ searchValueEvent: value?.trim() });
    }
  };

  /**
   * Applies a report status-marking socket update to the reports table. Clones the list and finds the report by id, then overwrites its statusReport and lastStatusReportAt only when the message timestamp is newer than the stored one or none was stored, leaving state untouched otherwise.
   *
   * @param msg - Socket message with the report id, its status and the status timestamp.
   * @returns Nothing.
   */
  const handleMarkStatusReportEvent = (msg: MarkStatusReportSocketMessage) => {
    setState((prevState) => {
      const clonedListReports = _.cloneDeep(prevState.listReports);
      /**
       * The report entry inside the cloned reports list whose id matches the incoming socket message. Resolved with a lodash find over clonedListReports so the matched object can be mutated in place.
       *
       * @returns Matching report object, or undefined when no id matches.
       */
      const ele = _.find(clonedListReports, (x) => x.id === msg.id);
      if (ele) {
        const shouldUpdate = ele.lastStatusReportAt
          ? dayjs(msg.lastStatusReportAt).isAfter(dayjs(ele.lastStatusReportAt))
          : true;
        if (shouldUpdate) {
          ele.statusReport = msg.statusReport;
          ele.lastStatusReportAt = msg.lastStatusReportAt;
          return { listReports: clonedListReports };
        }
      }
      return {};
    });
  };

  /**
   * Applies a report-updated socket message to the reports table. Ignores messages without a status, matches the row by id plus an identical non-nil isNonPhi flag so PHI and non-PHI variants stay distinct, then writes the new status and, when the message carries it, the background-rendering flag.
   *
   * @param msg - Socket message with the report id, PHI flag, status and rendering flag.
   * @returns Nothing.
   */
  const handleReportUpdatedEvent = (msg: ReportUpdatedSocketMessage) => {
    setState((prevState) => {
      if (!msg.status) return {};

      const clonedListReports = _.cloneDeep(prevState.listReports);
      /**
       * The report entry in the cloned list matching the socket message by id and by an identical, non-nil isNonPhi flag. Found with lodash find so PHI and non-PHI variants of the same report are not confused.
       *
       * @returns Matching report object, or undefined when no entry qualifies.
       */
      const targetReport = _.find(
        clonedListReports,
        (x) =>
          x.id === msg.id &&
          !_.isNil(x.isNonPhi) &&
          !_.isNil(msg.isNonPhi) &&
          x.isNonPhi === msg.isNonPhi,
      );

      if (targetReport) {
        targetReport.status = msg.status;
        if ('isRenderingBackground' in msg) {
          targetReport.isRenderingBackground = msg.isRenderingBackground;
        }
        return { listReports: clonedListReports };
      }
      return {};
    });
  };

  /**
   * The reports of the active tab, formatted for the table and sorted newest-first. Filters state.listReports to Study/Interim types for the summary tab or Notification/Event types otherwise, keeps only entries with an inbox date (sent from Call Center), maps each through formatReport and orders by lastModified descending.
   *
   * @returns Formatted report rows ordered by lastModified descending.
   */
  const dataReportT = _.orderBy(
    _.map(
      _.filter(
        state.listReports || [],
        (x) =>
          (isStudyReport
            ? x.type === 'Study' || x.type === 'Interim'
            : x.type === 'Notification' || x.type === 'Event') && !!x.inbox?.date, // *: Sent from Call Center reports
      ),
      (report) => formatReport(report),
    ),
    'lastModified',
    'desc',
  );

  const searchValue = isStudyReport
    ? state.searchValueSummary
    : isNotificationReport
      ? state.searchValueNotification
      : state.searchValueEvent;
  /**
   * The formatted report rows narrowed by the active search term. Memoized over searchValue and dataReportT: the literal search 'eou' keeps only rows without a reportId, any other term matches the zero-padded event friendlyId for event rows or reportId otherwise, and an empty term returns the full list.
   *
   * @returns Report rows matching the current search term.
   */
  const dataReport = useMemo(
    () =>
      searchValue
        ? _.filter(dataReportT, (x) =>
            _.lowerCase(searchValue) === 'eou'
              ? !x.reportId
              : zeroPad(x.type === 'Event' ? x.event?.friendlyId : x.reportId).includes(
                  searchValue,
                ),
          )
        : dataReportT,
    [searchValue, dataReportT],
  );

  useEffect(() => {
    if (data.reports) {
      /**
       * The study's reports deep-cloned and run through formatReport. Built from data.reports so the table rows carry study, patient and formatted date fields without mutating the source data.
       *
       * @returns Array of formatted report rows.
       */
      const listReports = _.cloneDeep(data.reports).map((report) => formatReport(report));
      setState({ listReports });
    }
  }, [data.reports, data.isNonPhi]);

  useEffect(() => {
    if (shouldLoadVerifiedReport && dataReport.length > 0) {
      /**
       * The end-of-use report in the formatted report list that has already been verified. Selected with lodash find over dataReport for the first entry of type 'Study' carrying an inbox date. A report of a CardioOne study raises the CardioOne report notice instead of being navigated to.
       *
       * @returns Matching verified study report, or undefined when none exists.
       */
      const verifiedReport = _.find(dataReport, (x) => x.type === 'Study' && !!x.inbox?.date);
      if (verifiedReport && isC1cStudy(data)) {
        emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_C1C_REPORT);
      } else if (verifiedReport) {
        navigate(`/studies/inbox/${verifiedReport.id}`);
      }
      resetShouldLoadVerifiedReport();
    }
  }, [dataReport, shouldLoadVerifiedReport]);

  useEmitter(EMITTER_CONSTANTS.MARK_STATUS_REPORT, handleMarkStatusReportEvent, [
    state.listReports,
  ]);

  useEmitter(EMITTER_CONSTANTS.REPORT_UPDATED, handleReportUpdatedEvent, [state.listReports]);

  return {
    state,
    setState,
    isStudyReport,
    isNotificationReport,
    dataReport,
    navigate,
    onClickReport,
    handleTableChange,
    onChangeSearch,
    searchValue,
  };
};
